import type { QueryTypeKey } from "../types/ragChatTypes";

export const QUERY_TYPE_BADGE: Record<
  string,
  { label: string; chipColor: "success" | "warning" | "secondary" | "primary" }
> = {
  instructions: { label: "Instructions", chipColor: "success" },
  diagnosis: { label: "Diagnosis", chipColor: "warning" },
  product_information: { label: "Product info", chipColor: "secondary" },
  finding_location: { label: "Finding location", chipColor: "primary" },
};

export function badgeForQueryType(q: QueryTypeKey) {
  if (!q) return null;
  return QUERY_TYPE_BADGE[q] ?? null;
}

/** Canonical assistant answer block order (see RagAssistantMessageCard). */
export const ASSISTANT_ANSWER_SECTION_ORDER = [
  "safety_alerts",
  "answer_and_diagrams",
  "options",
  "videos",
  "tables",
  "related_questions",
  "alternative_pdfs",
  "sources",
] as const;

export const ALERT_LEVEL_ORDER: Record<string, number> = {
  danger: 0,
  warning: 1,
  caution: 2,
  note: 3,
};

/** Step number pill — matches Step Card badge (#0084F4 / gableGreen). */
export const STEP_BADGE_PILL_SX = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  height: 22,
  px: 1,
  borderRadius: 999,
  bgcolor: "#0084F4",
  color: "common.white",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: 0.01,
} as const;

/** Procedure section heading pill — same shape as step badge, slightly stronger blue. */
export const PROCEDURE_SECTION_BADGE_PILL_SX = {
  ...STEP_BADGE_PILL_SX,
  bgcolor: "#0075E5",
} as const;

/**
 * Chat answer type scale — map structure to these roles, do not hardcode sizes.
 * title (h2) → heading (h3) → body → meta.
 * Pills stay for short badges only (Step 1, DTC code).
 */
export const ANSWER_TITLE_SX = {
  color: "text.primary",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.35,
  wordBreak: "break-word",
} as const;

export const ANSWER_HEADING_SX = {
  color: "text.primary",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.35,
  wordBreak: "break-word",
} as const;

export const ANSWER_BODY_SX = {
  color: "text.primary",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
} as const;

export const ANSWER_META_SX = {
  color: "text.secondary",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3,
} as const;

export const ANSWER_COUNT_CHIP_SX = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  px: 1,
  py: 0.25,
  borderRadius: 999,
  bgcolor: "grey.200",
  ...ANSWER_META_SX,
  fontWeight: 700,
} as const;

/** Visual tokens for safety notice cards (left-accent panel layout). */
export const ALERT_CARD_STYLES: Record<
  string,
  {
    accent: string;
    bg: string;
    labelColor: string;
    textColor: string;
    label: string;
  }
> = {
  danger: {
    accent: "#dc2626",
    bg: "#fef2f2",
    labelColor: "#dc2626",
    textColor: "#1f2937",
    label: "DANGER",
  },
  warning: {
    accent: "#ea580c",
    bg: "#fff7ed",
    labelColor: "#ea580c",
    textColor: "#1f2937",
    label: "WARNING",
  },
  caution: {
    accent: "#ca8a04",
    bg: "#fefce8",
    labelColor: "#ca8a04",
    textColor: "#1f2937",
    label: "CAUTION",
  },
  note: {
    accent: "#2563eb",
    bg: "#eff6ff",
    labelColor: "#2563eb",
    textColor: "#1f2937",
    label: "NOTE",
  },
};

export const ALERT_SEVERITY: Record<string, "error" | "warning" | "info"> = {
  danger: "error",
  warning: "warning",
  caution: "warning",
  note: "info",
};
